import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Download,
  Loader2,
  Printer,
  Grid3X3,
  Table2,
  Info,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Money({ value, className = "" }) {
  if (value === null || value === undefined) return "—";
  const v = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(v)) return "—";
  return <span className={className}>${v.toFixed(2)}</span>;
}

const STACKING_LABEL = {
  highest_only: "Highest discount only — discounts don't stack",
  club_digital_stack: "Club + Digital membership stack",
  full_stack: "All membership discounts stack",
};

export default function AdminFeeMatrix() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termWeeks, setTermWeeks] = useState(10);
  const [includeDM, setIncludeDM] = useState(true);
  const [includeRegFee, setIncludeRegFee] = useState(true);
  const [rateTableOptions, setRateTableOptions] = useState([]);
  // Selection is keyed by `${term_id}::${rate_table_id}` so that two terms
  // pointing to the same rate-table id (e.g. live row + snapshot copies) can
  // still be picked apart by the admin.
  const [selectedKey, setSelectedKey] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const matrixRef = useRef(null);

  // Resolve the currently selected option from the picker key.
  const selectedOption = useMemo(
    () => rateTableOptions.find((o) => `${o.term_id}::${o.rate_table_id}` === selectedKey),
    [rateTableOptions, selectedKey]
  );

  // Pull the list of rate tables linked to any term (active or hidden) so the
  // admin can flip between them for comparison. The default selection is the
  // backend's own default (most recent non-hidden term), which we mirror once
  // the matrix returns its `term_id`/`rate_table_id`.
  const loadOptions = async () => {
    try {
      const res = await axios.get(`${API}/academy/admin/fee-matrix/available-rate-tables`, {
        withCredentials: true,
      });
      setRateTableOptions(res.data.options || []);
    } catch (e) {
      console.error(e);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        term_weeks: termWeeks,
        include_digital_membership: includeDM,
        include_registration_fee: includeRegFee,
      };
      if (selectedOption) {
        params.term_id = selectedOption.term_id;
        params.rate_table_id = selectedOption.rate_table_id;
      }
      const res = await axios.get(`${API}/academy/admin/fee-matrix`, {
        params,
        withCredentials: true,
      });
      setMatrix(res.data);
      // First load: sync our selectedKey with the backend default so the
      // banner + dropdown reflect what we're actually rendering.
      if (!selectedKey && res.data.term_id && res.data.rate_table_id) {
        setSelectedKey(`${res.data.term_id}::${res.data.rate_table_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load matrix");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  useEffect(() => {
    load();
  }, [termWeeks, includeDM, includeRegFee, selectedKey]);

  const printPage = () => window.print();

  const downloadPNG = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      if (!matrixRef.current) return;
      const canvas = await html2canvas(matrixRef.current, {
        backgroundColor: "#0a0f1a",
        scale: 2,
      });
      const link = document.createElement("a");
      link.download = `fee-matrix-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate image");
    }
  };

  const stackingLabel =
    matrix?.discount_stacking_mode && STACKING_LABEL[matrix.discount_stacking_mode]
      ? STACKING_LABEL[matrix.discount_stacking_mode]
      : matrix?.discount_stacking_mode || "—";

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5 print:hidden">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" className="text-white" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Grid3X3 className="w-6 h-6 text-purple-400" />
              Fee Matrix
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={downloadPNG}
              variant="outline"
              size="sm"
              className="border-emerald-500/40 text-emerald-400"
              data-testid="download-png-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PNG
            </Button>
            <Button
              onClick={printPage}
              variant="outline"
              size="sm"
              className="border-blue-500/40 text-blue-400"
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print / Save PDF
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Rate-table banner — always visible so admins know which table the
            matrix reflects. Click opens a picker so they can compare other
            tables linked to active or hidden terms. */}
        <Card
          className="p-4 mb-4 bg-gradient-to-r from-purple-900/30 to-slate-900/40 border-purple-500/30 print:hidden"
          data-testid="rate-table-banner"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-200">
                This matrix is using{" "}
                <span className="font-semibold text-white" data-testid="banner-rate-table-name">
                  {matrix?.rate_table_name || "—"}
                </span>
                {matrix?.term_name && (
                  <>
                    {" "}from term{" "}
                    <span className="font-semibold text-white" data-testid="banner-term-name">
                      {matrix.term_name}
                    </span>
                  </>
                )}
                {matrix?.rate_table_source === "snapshot" && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                    snapshot
                  </span>
                )}
                {matrix?.rate_table_source === "live" && (
                  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                    live
                  </span>
                )}
                .
              </div>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="text-sm text-purple-300 hover:text-purple-200 underline underline-offset-2 mt-1"
                data-testid="open-rate-table-picker-btn"
              >
                Click here to change the rate table or compare previous rate tables
              </button>
              {matrix?.discount_stacking_mode && (
                <div className="text-xs text-slate-400 mt-1">
                  Stacking rule: <span className="text-slate-300">{stackingLabel}</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Controls */}
        <Card className="p-4 mb-4 bg-slate-900/50 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-white text-sm">Term weeks</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={termWeeks}
                onChange={(e) => setTermWeeks(parseInt(e.target.value) || 10)}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="term-weeks-input"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded bg-slate-800/50">
              <Label className="text-white text-sm">Include digital membership</Label>
              <Switch checked={includeDM} onCheckedChange={setIncludeDM} data-testid="include-dm-switch" />
            </div>
            <div className="flex items-center justify-between p-3 rounded bg-slate-800/50">
              <Label className="text-white text-sm">Include registration fee</Label>
              <Switch
                checked={includeRegFee}
                onCheckedChange={setIncludeRegFee}
                data-testid="include-regfee-switch"
              />
            </div>
          </div>
        </Card>

        {loading && (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-10 h-10 animate-spin text-purple-400" />
          </div>
        )}

        {!loading && matrix && (
          <div ref={matrixRef} className="bg-slate-950 p-6 rounded-lg print:bg-white print:text-black">
            <h2 className="text-2xl font-bold text-white mb-1 print:text-black" data-testid="matrix-title">
              SCC Academy - Term Fee Matrix
            </h2>
            <p className="text-sm text-slate-400 mb-1 print:text-slate-700">
              Rate table: <span className="text-white print:text-black font-semibold">{matrix.rate_table_name}</span>
              {matrix.term_name && <> · Term: <span className="text-white print:text-black font-semibold">{matrix.term_name}</span></>}
            </p>
            <p className="text-sm text-slate-400 mb-1 print:text-slate-700">
              {termWeeks}-week term · Base ${matrix.base_price_per_session_per_week}/session/week ·
              Registration fee {matrix.registration_fee_per_child > 0 ? `$${matrix.registration_fee_per_child}/child` : "—"} ·
              Digital Membership ${matrix.digital_membership_price}
            </p>
            <p className="text-xs text-slate-500 mb-2 print:text-slate-600">
              Stacking rule: {stackingLabel}
              {matrix.include_digital_membership && !matrix.member_benefits_from_dm && (
                <> · DM is redundant for SCC members under this rule (no extra discount, no $126 fee charged in member rows).</>
              )}
            </p>
            <p className="text-xs text-slate-500 mb-6 print:text-slate-600">
              Includes: {includeRegFee ? "Reg fee" : "(no reg fee)"} · {includeDM ? "Digital membership" : "(no DM)"} ·
              Sibling discounts automatically applied per rate table.
            </p>

            {["non_member", "scc_member"].map((type) => (
              <div key={type} className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-white print:text-black flex items-center gap-2">
                  {type === "scc_member" ? (
                    <span className="px-2 py-0.5 text-xs rounded bg-emerald-500/20 text-emerald-400">SCC Member</span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded bg-slate-500/20 text-slate-300">Non-Member</span>
                  )}
                  Total fees per family
                </h3>
                <table className="w-full border-collapse text-white print:text-black" data-testid={`matrix-${type}`}>
                  <thead>
                    <tr>
                      <th className="p-3 text-left bg-slate-800/70 print:bg-slate-200 border border-slate-700 print:border-slate-400 text-sm">
                        Children
                      </th>
                      <th className="p-3 bg-slate-800/70 print:bg-slate-200 border border-slate-700 print:border-slate-400 text-sm">
                        1× per week
                      </th>
                      <th className="p-3 bg-slate-800/70 print:bg-slate-200 border border-slate-700 print:border-slate-400 text-sm">
                        2× per week
                      </th>
                      <th className="p-3 bg-slate-800/70 print:bg-slate-200 border border-slate-700 print:border-slate-400 text-sm">
                        3× per week
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.matrix[type].map((row) => (
                      <tr key={row.num_children}>
                        <td className="p-3 border border-slate-700 print:border-slate-400 bg-slate-900/30 print:bg-slate-100 font-semibold">
                          {row.num_children} child{row.num_children > 1 ? "ren" : ""}
                        </td>
                        {row.cells.map((cell) => (
                          <td
                            key={cell.sessions_per_week}
                            className="p-3 border border-slate-700 print:border-slate-400 text-center"
                          >
                            <div className="text-xl font-bold text-emerald-400 print:text-emerald-700">
                              <Money value={cell.grand_total} />
                            </div>
                            <div className="text-[10px] text-slate-400 print:text-slate-600 mt-1 leading-snug">
                              Base <Money value={cell.base_total} />
                              {cell.registration_fee_total > 0 && (
                                <>
                                  {" "}+ Reg <Money value={cell.registration_fee_total} />
                                </>
                              )}
                              {cell.digital_membership > 0 && (
                                <>
                                  {" "}+ DM <Money value={cell.digital_membership} />
                                </>
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            <p className="text-xs text-slate-500 print:text-slate-600 mt-4">
              Generated {new Date().toLocaleString()} · Figures are per family (all children combined).
              Sibling discount is applied automatically from the 2nd child onwards according to the rate table.
            </p>
          </div>
        )}
      </div>

      {/* Rate-table picker — lists every rate table linked to any term
          (active or hidden) so the admin can compare. The list is sorted by
          term start_date DESC so the most relevant table floats first. */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Table2 className="w-5 h-5 text-purple-400" />
              Choose Rate Table
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Pick the rate table you want the matrix to render against. Only rate tables linked to a
              term (active or hidden) are listed.
            </DialogDescription>
          </DialogHeader>
          <div
            className="max-h-[400px] overflow-y-auto divide-y divide-slate-800 border border-slate-800 rounded"
            data-testid="rate-table-picker-list"
          >
            {rateTableOptions.length === 0 && (
              <div className="p-4 text-sm text-slate-400 text-center">
                No rate tables linked to any term yet.
              </div>
            )}
            {rateTableOptions.map((opt) => {
              const key = `${opt.term_id}::${opt.rate_table_id}`;
              const isActive = key === selectedKey;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => {
                    setSelectedKey(key);
                    setPickerOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    isActive ? "bg-purple-900/30" : "hover:bg-slate-800/60"
                  }`}
                  data-testid={`rate-table-option-${opt.term_id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-white truncate">{opt.rate_table_name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        Term: <span className="text-slate-200">{opt.term_name || "—"}</span>
                        {opt.term_start_date && (
                          <> · Start {new Date(opt.term_start_date).toLocaleDateString()}</>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span
                        className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                          opt.source === "snapshot"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-amber-500/20 text-amber-300"
                        }`}
                      >
                        {opt.source}
                      </span>
                      {opt.is_hidden && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300">
                          hidden
                        </span>
                      )}
                      {opt.is_finalized && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
                          finalized
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPickerOpen(false)}
              className="border-slate-600"
              data-testid="rate-table-picker-cancel"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
